import type { MouseEventHandler, ReactNode } from 'react';

import type { Nil, Unit, UserPreferredUnit } from '@core';

import { Stack, type BoxProps } from '../../Layout';
import { SelectBox, type GraphColorVariants } from '../../SelectBox/SelectBox';
import { ParameterCard } from '../ParameterCard';
import { ParameterStats } from '../ParameterStats';
import { useParameters } from '../useParameters';

import type { CorridorBounds } from '../types';

export type SwingFoundationParameterSelectionCardProps = {
    title: string;
    unit: Unit;
    userPreferredUnit: UserPreferredUnit;
    value: number | Nil;
    comparisonValue?: number | Nil;
    corridorParameterMargins?: CorridorBounds | Nil;
    goalCorridor?: CorridorBounds | Nil;
    mean?: number | Nil;
    stdDev?: number | Nil;
    menuItems?: ReactNode;
    shadow?: BoxProps['shadow'];
    isSelected?: boolean;
    selectColor?: GraphColorVariants;
    showCorridors?: boolean;
    onClick?: MouseEventHandler<HTMLDivElement>;
};

export const SwingFoundationParameterSelectionCard = ({
    title,
    unit,
    userPreferredUnit,
    value,
    comparisonValue,
    corridorParameterMargins,
    goalCorridor,
    mean,
    stdDev,
    menuItems,
    shadow,
    isSelected,
    selectColor,
    showCorridors = true,
    onClick,
}: SwingFoundationParameterSelectionCardProps) => {
    const converted = useParameters({
        unit,
        value,
        comparisonValue,
        corridorParameterMargins,
        goalCorridorParameterMargins: goalCorridor,
        userPreferredUnit,
        stdDev,
        mean,
    });

    const indicatorColor = goalCorridor ? converted.goalIndicatorColor : converted.corridorIndicatorColor;
    const startSlot =
        isSelected === undefined ? undefined : (
            <SelectBox color="gray" selected={isSelected} selectColor={selectColor} />
        );
    const endSlot = menuItems ? <ParameterCard.Menu>{menuItems}</ParameterCard.Menu> : undefined;

    return (
        <Stack gap={0}>
            <ParameterCard.Root
                small
                shadow={shadow}
                onClick={onClick}
                corridors={
                    showCorridors ? (
                        <ParameterCard.Corridors
                            corridorParameterMargins={converted.corridorParameterMargins}
                            goalCorridor={converted.goalCorridor}
                            unit={converted.unit}
                            value={converted.value}
                            indicatorColor={indicatorColor}
                        />
                    ) : undefined
                }
            >
                <ParameterCard.Title startSlot={startSlot} endSlot={endSlot}>
                    {title}
                </ParameterCard.Title>
                <ParameterCard.Values>
                    <ParameterCard.Value
                        displayUnit={converted.unit}
                        symbol={converted.symbol}
                        value={converted.value}
                        indicatorColor={indicatorColor}
                    />
                    <ParameterCard.Comparison
                        displayUnit={converted.unit}
                        symbol={converted.symbol}
                        value={converted.comparison}
                    />
                </ParameterCard.Values>
            </ParameterCard.Root>
            <ParameterStats mean={converted.mean} stdDev={converted.stdDev} />
        </Stack>
    );
};
